import { addDays, formatDistanceToNow } from 'date-fns'
import { type SelectOption } from '#app/components/select-field-control.tsx'
import { cn } from './misc';
import { type ObjectValues } from './typescript-helpers'

// const options: Intl.DateTimeFormatOptions = {
// 	year: 'numeric', // 4-digit year (e.g., 2023)
// 	month: 'short', // Abbreviated month name (e.g., Jan, Feb)
// 	day: 'numeric', // Day of the month (e.g., 1-31)
// 	hour: '2-digit', // Hours in 2-digit format (e.g., 01-12 or 00-23)
// 	minute: '2-digit', // Minutes in 2-digit format (e.g., 00-59)
// 	hour12: true, // Use 12-hour clock (AM/PM)
// }

export const formatDate = (
	date: Date | string,
	options?: Intl.DateTimeFormatOptions,
): string => {
	if (typeof date === 'string') {
		date = new Date(date)
	}

	if (isNaN(date.getTime())) {
		console.error('Invalid date string:', date)
		return 'Invalid Date'
	}

	// Example output: "Jan 15, 2023, 03:30 PM"
	return date.toLocaleString(undefined, options)
}

export const formatDateAndTimeAt = (
	dateAt: Date | string | null,
	noDateText = 'Not scheduled',
): string => {
	if (!dateAt) return noDateText

	const dateObj = typeof dateAt === 'string' ? new Date(dateAt) : dateAt

	const formattedDate = dateObj?.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})
	const formattedTime = dateObj.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
	})

	return `${formattedDate} at ${formattedTime}`
}

export const formatDateTable: Intl.DateTimeFormatOptions = {
	month: 'short',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	hour12: true,
}

export const EntityTableDateColumn = ({
	date,
	format = {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
		timeZone: 'UTC',
	},
	stackDateAndTime = false,
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement> & {
	date: Date
	format?: Intl.DateTimeFormatOptions
	stackDateAndTime?: boolean
}) => {
	// Convert date string to Date object if needed
	const dateObj = typeof date === 'string' ? new Date(date) : date

	if (stackDateAndTime) {
		const formattedDate = dateObj?.toLocaleString('en-US', {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
		})
		const formattedTime = dateObj.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		})

		return (
			<div {...props} className={cn("flex flex-col items-end gap-0", className)}>
				<span className="text-xs text-secondary-foreground">{formattedDate}</span>
				<span className="text-xs text-secondary-foreground">{formattedTime}</span>
			</div>
		)
	}

	// Format the date in UTC to ensure consistency between server and client
	const formattedDate = dateObj?.toLocaleString('en-US', {
		...format,
	})

	return (
		<div className={cn("flex items-center justify-end text-xs text-muted-foreground", className)}>
			<span>{formattedDate}</span>
		</div>
	)
}

export const formatDayAndShortDate = (date: Date) => {
	return date.toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'numeric',
		day: 'numeric',
	})
}

export const formatDateOfBirth = (date: Date) => {
	return date.toLocaleDateString('en-US', {
		timeZone: 'UTC', // This keeps the date fixed to UTC, avoiding timezone shifts.
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	})
}

export const formatDateForInput = (date: Date) => {
	return new Date(date).toISOString().split('T')[0]
}

// get a really short date string
export const base36Date = (date?: Date) => {
	return date ? date.getTime().toString(36) : Date.now().toString(36)
}

// don't really need, but cool
export const base36DateToOriginal = (base36String: string) => {
	return new Date(parseInt(base36String, 36))
}

export const DateTimeFormats = {
	API: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
	API_UTC: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
	API_TIMEZONE_OFFSET: "yyyy-MM-dd'T'HH:mm:ssxxx",
	DISPLAY_DATE: 'MMM d, yyyy',
	DISPLAY_TIME: 'h:mm a',
	DISPLAY_DATETIME: 'MMM d, yyyy h:mm a',
	INPUT_DATE: 'yyyy-MM-dd',
	INPUT_TIME: 'HH:mm',
} as const

export const convertToUTC = (date: Date | string): Date => {
	const d = new Date(date)
	return new Date(
		Date.UTC(
			d.getFullYear(),
			d.getMonth(),
			d.getDate(),
			d.getHours(),
			d.getMinutes(),
			d.getSeconds(),
			d.getMilliseconds(),
		),
	)
}

export const formatInTimeZone = (
	date: Date | string,
	timeZone: string,
	format: keyof typeof DateTimeFormats,
): string => {
	const d = new Date(date)
	return d?.toLocaleString('en-US', {
		timeZone,
		...getFormatOptions(format),
	})
}

const getFormatOptions = (
	format: keyof typeof DateTimeFormats,
): Intl.DateTimeFormatOptions => {
	switch (format) {
		case 'DISPLAY_DATE':
			return {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}
		case 'DISPLAY_TIME':
			return {
				hour: 'numeric',
				minute: 'numeric',
				hour12: true,
			}
		case 'DISPLAY_DATETIME':
			return {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: 'numeric',
				hour12: true,
			}
		default:
			return {}
	}
}

/**
 * Converts a date to UTC while preserving the local time values
 * @param input - Date object to convert to UTC
 * @returns A new Date object with the same time values but in UTC
 */
export const treatAsUTC = (input: Date): Date => {
	return new Date(
		Date.UTC(
			input.getFullYear(),
			input.getMonth(),
			input.getDate(),
			input.getHours(),
			input.getMinutes(),
			input.getSeconds(),
			input.getMilliseconds(),
		),
	)
}

/**
 * Validates if a timezone string is valid by attempting to create a DateTimeFormat with it
 * @param timeZone - Timezone string to validate (e.g. 'America/New_York')
 * @returns true if the timezone is valid, false otherwise
 */
export const isValidTimezone = (timeZone: string): boolean => {
	try {
		Intl.DateTimeFormat(undefined, { timeZone: timeZone })
		return true
	} catch (error) {
		console.error('Invalid timeZone:', error)
		return false
	}
}

/**
 * Compares a date with another date in a specific timezone
 * @param date - First date to compare (string or Date object)
 * @param compareDate - Second date to compare against (string or Date object), defaults to current time
 * @param timeZone - IANA timezone string (e.g., 'America/New_York')
 * @returns Object containing comparison results and formatted dates
 * @throws Error if timezone is invalid
 */
export function compareDateWithTimezone({
	date,
	compareDate = new Date(),
	timeZone,
}: {
	date: Date | string
	compareDate?: Date | string
	timeZone: string
}): {
	isBeforeCompareDate: boolean
	isAfterCompareDate: boolean
	isEqualToCompareDate: boolean
	parsedUTCTime: string
	parsedCompareUTCTime: string
	dateInLocalTime: String
	compareDateInLocalTime: String
	timeZone: string
} {
	// Validate timeZone
	if (!isValidTimezone(timeZone)) {
		throw new Error(`Invalid timeZone: ${timeZone}`)
	}

	// Convert the input date, treating the time as UTC
	const utcDate = typeof date === 'string' ? new Date(date) : date
	const utcDateTime = treatAsUTC(utcDate)

	// Get compare/current time in specified timeZone
	const localOptions = { timeZone }
	const compareDateInTimezone = new Date(
		compareDate?.toLocaleString('en-US', localOptions),
	)

	return {
		isBeforeCompareDate:
			utcDateTime.getTime() < compareDateInTimezone.getTime(),
		isAfterCompareDate: utcDateTime.getTime() > compareDateInTimezone.getTime(),
		isEqualToCompareDate:
			utcDateTime.getTime() === compareDateInTimezone.getTime(),
		parsedUTCTime: utcDateTime.toUTCString(),
		parsedCompareUTCTime: compareDateInTimezone.toUTCString(),
		dateInLocalTime: utcDateTime?.toLocaleString('en-US', localOptions),
		compareDateInLocalTime: compareDateInTimezone?.toLocaleString(
			'en-US',
			localOptions,
		),
		timeZone,
	}
}

export const convertDateWithTimezoneToUTC = (date: Date): Date => {
	return treatAsUTC(date)
}

export const dateFormatDistanceToNow = (date: Date) => {
	return formatDistanceToNow(date, { addSuffix: true })
}

export const getDateFromDaysFromToday = (daysFromToday: number) => {
	return addDays(new Date(), daysFromToday)
}

export const TIME_OF_DAY = {
	MORNING: 'morning',
	// NOON: 'noon',
	AFTERNOON: 'afternoon',
	EVENING: 'evening',
	// NIGHT: 'night',
} as const
export type ITimeOfDayOption = ObjectValues<typeof TIME_OF_DAY>
export const TIME_OF_DAY_OPTIONS_VALUES: string[] = Object.values(TIME_OF_DAY)

export const getTimeFromTimeOfDay = (timeOfDay: ITimeOfDayOption): string => {
	switch (timeOfDay) {
		case TIME_OF_DAY.MORNING:
			return '9:00'
		// case TIME_OF_DAY.NOON:
		// 	return '12:00'
		case TIME_OF_DAY.AFTERNOON:
			return '15:00'
		case TIME_OF_DAY.EVENING:
			return '18:00'
		// case TIME_OF_DAY.NIGHT:
		// 	return '20:00'
		default:
			throw new Error(`Invalid time of day: ${timeOfDay}`)
	}
}

export const updateDateWithTimeOfDay = (
	date: Date,
	timeOfDay: ITimeOfDayOption,
): Date => {
	const [hours] = getTimeFromTimeOfDay(timeOfDay).split(':')
	if (!hours) {
		throw new Error(`Invalid time of day: ${timeOfDay}`)
	}
	const newDate = new Date(date)
	newDate.setHours(parseInt(hours, 10), 0, 0, 0)
	// newDate.setUTCHours(parseInt(hours, 10), 0, 0, 0)
	return newDate
}

export const getTimeOfDayFromDate = (date: Date): ITimeOfDayOption => {
	const hours = date.getHours()
	if (hours <= 9) return TIME_OF_DAY.MORNING
	// if (hours <= 12) return TIME_OF_DAY.NOON
	if (hours <= 15) return TIME_OF_DAY.AFTERNOON
	if (hours <= 18) return TIME_OF_DAY.EVENING
	return TIME_OF_DAY.EVENING
	// return TIME_OF_DAY.NIGHT
}

export const getTimeOfDayLabelFromDate = (date: Date): string => {
	const timeOfDay = getTimeOfDayFromDate(date)
	return timeOfDay ?? 'Invalid time of day'
}

export const dateIsBeforeToday = (date: Date): boolean => {
	return date.getTime() < Date.now() - 1000 * 60 * 60 * 24
}

export const getUpcomingActivityDateOptions = (): {
	label: string
	value: number
}[] => {
	const today = new Date()
	const currentHour = today.getHours()

	const options = [
		{
			label: 'Tomorrow',
			value: 1,
		},
		...[2, 3, 4, 5, 6].map((daysFromToday) => {
			const date = new Date(today)
			date.setDate(today.getDate() + daysFromToday)
			const dateLabel = formatDayAndShortDate(date)
			return {
				label: dateLabel,
				value: daysFromToday,
			}
		}),
	]

	// include today if before 6pm
	if (currentHour < 23) {
		options.unshift({
			label: 'Today',
			value: 0,
		})
	}

	return options
}

export const getUpcomingActivityTimeOptions = ({
	scheduledAtDate,
}: {
	scheduledAtDate?: Date
}): string[] => {
	const now = new Date()
	const currentHour = now.getHours()

	// If scheduledAtDate is today, filter out past times
	if (
		scheduledAtDate &&
		scheduledAtDate.toDateString() === now.toDateString()
	) {
		return TIME_OF_DAY_OPTIONS_VALUES.filter((timeOfDay) => {
			if (timeOfDay === 'morning' && currentHour >= 9) return false
			if (timeOfDay === 'afternoon' && currentHour >= 15) return false
			if (timeOfDay === 'evening' && currentHour >= 18) return false
			return true
		})
	}

	// Otherwise return all time options
	return TIME_OF_DAY_OPTIONS_VALUES
}

export const getTimeOptionsHalfHour = ({
	scheduledAtDate,
}: {
	scheduledAtDate?: Date
}): SelectOption[] => {
	const options: SelectOption[] = []
	const now = new Date()
	const currentHour = now.getHours()
	const currentMinutes = now.getMinutes()

	for (let hour = 0; hour < 24; hour++) {
		const hourStr =
			hour === 0 ? '12' : hour > 12 ? (hour - 12).toString() : hour.toString()
		const meridiem = hour < 12 ? 'am' : 'pm'

		// If scheduledAtDate is today, skip past times
		if (
			scheduledAtDate &&
			scheduledAtDate.toDateString() === now.toDateString()
		) {
			if (hour < currentHour) continue

			if (hour === currentHour) {
				// Only add :00 if we haven't passed it
				if (currentMinutes < 0) {
					options.push({
						id: `${hour.toString().padStart(2, '0')}:00`,
						name: `${hourStr}:00 ${meridiem}`,
					})
				}
				// Only add :30 if we haven't passed it
				if (currentMinutes < 30) {
					options.push({
						id: `${hour.toString().padStart(2, '0')}:30`,
						name: `${hourStr}:30 ${meridiem}`,
					})
				}
				continue
			}
		}

		// Add both options for future times
		options.push({
			id: `${hour.toString().padStart(2, '0')}:00`,
			name: `${hourStr}:00 ${meridiem}`,
		})

		options.push({
			id: `${hour.toString().padStart(2, '0')}:30`,
			name: `${hourStr}:30 ${meridiem}`,
		})
	}

	return options
}
